import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";
import Loader from "components/Loader/loader";

import faqTableData from "layouts/faqManagement/data/faqTableData";
import { getFaqs, createFaq, updateFaq, deleteFaq } from "services/apiService";

const EMPTY_FORM = { question: "", answer: "" };

function FaqManagement() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    isDelete: false,
    faqId: "",
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await getFaqs();
      if (response?.success) {
        setFaqs(response?.data?.faqs || response?.data || []);
      }
    } catch (err) {
      console.error("Fetch FAQs failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleEdit = (faq) => {
    setEditingId(faq?.id);
    setForm({ question: faq?.question || "", answer: faq?.answer || "" });
    setFormOpen(true);
  };

  const handleDelete = (faqId) => {
    setAlert({
      open: true,
      type: "confirm",
      title: "Confirm Delete",
      message: "Are you sure you want to delete this FAQ?",
      isDelete: true,
      faqId,
    });
  };

  const onConfirmDelete = async () => {
    setLoading(true);
    try {
      const res = await deleteFaq(alert.faqId);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Failed to delete FAQ.",
          isDelete: false,
          faqId: "",
        });
      } else {
        setAlert({
          open: true,
          type: "success",
          title: "Deleted",
          message: res?.message || "FAQ deleted successfully.",
          isDelete: false,
          faqId: "",
        });
        fetchFaqs();
      }
    } catch (err) {
      console.error("Delete FAQ failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to delete FAQ.",
        isDelete: false,
        faqId: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Please enter both a question and an answer.",
        isDelete: false,
        faqId: "",
      });
      return;
    }

    setLoading(true);
    try {
      const res = editingId
        ? await updateFaq(editingId, { question: form.question, answer: form.answer })
        : await createFaq({
            question: form.question,
            answer: form.answer,
            sortOrder: faqs.length + 1,
          });

      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Something went wrong.",
          isDelete: false,
          faqId: "",
        });
        return;
      }

      setFormOpen(false);
      setAlert({
        open: true,
        type: "success",
        title: editingId ? "Updated" : "Added",
        message: res?.message || (editingId ? "FAQ updated successfully." : "FAQ added successfully."),
        isDelete: false,
        faqId: "",
      });
      fetchFaqs();
    } catch (err) {
      console.error("Save FAQ failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to save FAQ.",
        isDelete: false,
        faqId: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const { columns, rows } = useMemo(
    () =>
      faqTableData({
        faqList: faqs,
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [faqs]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loader />}

      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => {
          onConfirmDelete();
        }}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MDTypography variant="h6" color="white">
                  FAQ Management
                </MDTypography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAdd}
                  sx={{
                    color: "#2e7d32 !important",
                    backgroundColor: "#fff !important",
                    "&:hover": { backgroundColor: "#f1f1f1 !important" },
                  }}
                >
                  Add FAQ
                </Button>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  page={page}
                  onPageChange={setPage}
                />
              </MDBox>
            </Card>

            {/* ── Add / Edit FAQ Dialog ── */}
            <Dialog
              open={formOpen}
              onClose={() => setFormOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle>{editingId ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  margin="dense"
                  label="Question"
                  placeholder="Enter the frequently asked question..."
                  value={form.question}
                  onChange={(e) => setForm({ ...form, question: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  margin="dense"
                  label="Answer"
                  placeholder="Enter the answer shown to users..."
                  value={form.answer}
                  onChange={(e) => setForm({ ...form, answer: e.target.value })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setFormOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleFormSubmit}
                  sx={{
                    color: "#fff !important",
                    backgroundColor: "#2e7d32 !important",
                    "&:hover": { backgroundColor: "#1b5e20 !important" },
                  }}
                >
                  {editingId ? "Save Changes" : "Add FAQ"}
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>

    </DashboardLayout>
  );
}

export default FaqManagement;
